import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

function useKey(comboString) {
  const uiState = useSelector((state) => state.sift.ioManager.uiState);
  const uiStateTwo = useSelector((state) => state.inputs.uiState);
  const user = useSelector((state) => state.user);
  const projectManager = useSelector((state) => state.sift.projectManager);
  const searchVisible = useSelector((state) => state.inputs.uiState.search_visible);
  const showSfModal = useSelector((state) => state.inputs.uiState.show_sf_modal);

  const [pressed, setPressed] = useState(false);
  const stopActionRef = useRef(false);
  const comboRef = useRef(parseCombo(comboString));

  const mapHasFocus = useCallback(() => {
    return document.querySelector("canvas.mapboxgl-canvas") === document.activeElement;
  }, []);

  const stopConditions = useMemo(() => {
    return (
      user.funnel_visible ||
      user.login_visible ||
      uiState.bug_visible ||
      uiState.patch_visible ||
      uiState.helper_visible ||
      uiState.collabVisible ||
      uiState.show_live_report ||
      uiStateTwo.pm_modal_visible ||
      uiStateTwo.collab_modal_visible?.visible ||
      uiStateTwo.disable_hotkeys ||
      projectManager.modal_visible.visible ||
      showSfModal ||
      searchVisible
    );
  }, [
    user.funnel_visible,
    user.login_visible,
    uiState.bug_visible,
    uiState.patch_visible,
    uiState.helper_visible,
    uiState.collabVisible,
    uiState.show_live_report,
    uiStateTwo.pm_modal_visible,
    uiStateTwo.collab_modal_visible?.visible,
    uiStateTwo.disable_hotkeys,
    projectManager.modal_visible.visible,
    showSfModal,
    searchVisible,
  ]);

  useEffect(() => {
    stopActionRef.current = stopConditions;
  }, [stopConditions]);

  const matchCombo = useCallback((event) => {
    const combo = comboRef.current;
    return event.key.toLowerCase() === combo.key && event.ctrlKey === combo.ctrl && event.shiftKey === combo.shift && event.altKey === combo.alt && event.metaKey === combo.meta;
  }, []);

  const onDown = useCallback(
    (event) => {
      if (!mapHasFocus()) return;
      if (stopActionRef.current) return;
      if (matchCombo(event) && !pressed) {
        setPressed(true);
      }
    },
    [matchCombo, pressed, mapHasFocus]
  );

  const onUp = useCallback(
    (event) => {
      if (!mapHasFocus()) return;
      if (stopActionRef.current) return;
      if (matchCombo(event)) {
        setPressed(false);
      }
    },
    [matchCombo, mapHasFocus]
  );

  useEffect(() => {
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [onDown, onUp]);

  return pressed;
}

function parseCombo(combo) {
  const parts = combo.toLowerCase().split("+");
  return {
    ctrl: parts.includes("ctrl"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
    meta: parts.includes("meta") || parts.includes("cmd") || parts.includes("command"),
    key: parts.find((p) => !["ctrl", "shift", "alt", "meta", "cmd", "command"].includes(p)),
  };
}

const useKeyPressOnce = (targetKey) => {
  const [pressed, setPressed] = useState(false);
  const handledRef = useRef(false);

  useEffect(() => {
    const downHandler = ({ key }) => {
      if (key.toLowerCase() === targetKey.toLowerCase() && !handledRef.current) {
        setPressed(true);
        handledRef.current = true;
      }
    };

    const upHandler = ({ key }) => {
      if (key.toLowerCase() === targetKey.toLowerCase()) {
        handledRef.current = false;
        setPressed(false);
      }
    };

    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  }, [targetKey]);

  return pressed;
};

export { useKey, useKeyPressOnce };
